import { Button, DialogBody, DialogFooter, IconButton } from '@civilgrid/ui/components';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { archiveProject, editProject } from '~/api/projects2';
import { Action } from '~/authorizations';
import { copyGeometry, exportToKMZ } from '~/common/projectUtils';
import { ORG_SWITCHER_PROJECTS_KEY } from '~/common/queryKeys';
import { ProjectPhase } from '~/common/types';
import { Dropdown, type Item } from '~/components2/Dropdown';
import { deleteProjectMapBoundsInLocalStorage } from '~/components2/Map/helpers/mapBounds';
import Notify from '~/components2/Notify';
import { homeMapStore } from '~/pages/homeViewV2/store';
import rootStore from '~/store';
import type { ProjectWithDetails } from './common';

interface Props {
  project: ProjectWithDetails;
}

export default function MoreButton({ project }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const items: Item[] = [];

  // Export project as KMZ option
  items.push({
    type: 'simple',
    label: 'Export as KMZ',
    onClick: () => exportToKMZ(project),
  });

  // Copy as GeoJSON
  items.push({
    type: 'simple',
    label: 'Copy GeoJSON to Clipboard',
    onClick: () => copyGeometry(project),
  });

  // Complete/Re-open Project Option
  if (project.projectPhase !== ProjectPhase.Completed) {
    items.push({
      type: 'dialog',
      label: 'Mark Complete',
      isAllowedTo: Action.EditProjectDetails,
      dialogTitle: 'Confirm',
      dialogContent: (close) => (
        <>
          <DialogBody className="text-sm">
            The project will no longer be visible in the <span className="font-semibold">Active Projects</span> section.
            Are you sure?
          </DialogBody>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={close}
              className="min-w-20"
              data-testid="project-details-more-mark-complete-no"
            >
              No
            </Button>
            <Button
              variant="danger"
              className="min-w-20"
              data-testid="project-details-more-mark-complete-yes"
              onClick={async () => {
                close();
                await editProject(project.id, {
                  attributes: { projectPhase: ProjectPhase.Completed },
                });

                Notify.info({ message: 'Project Updated' });
                await queryClient.invalidateQueries({
                  queryKey: [...ORG_SWITCHER_PROJECTS_KEY, rootStore.currentOrganizationId],
                });
                await queryClient.invalidateQueries({ queryKey: ['projects'] });
                homeMapStore.setPopupParams(undefined);
                await deleteProjectMapBoundsInLocalStorage(project.id);
                navigate({ to: '/home' });
              }}
            >
              Yes
            </Button>
          </DialogFooter>
        </>
      ),
    });
  } else {
    items.push({
      type: 'dialog',
      label: 'Re-open Project',
      isAllowedTo: Action.EditProjectDetails,
      dialogTitle: 'Confirm',
      dialogContent: (close) => (
        <>
          <DialogBody className="text-sm">Are you sure?</DialogBody>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={close}
              className="min-w-20"
              data-testid="project-details-more-reopen-no"
            >
              No
            </Button>
            <Button
              variant="danger"
              className="min-w-20"
              data-testid="project-details-more-reopen-yes"
              onClick={async () => {
                close();
                await editProject(project.id, {
                  attributes: { projectPhase: ProjectPhase.PreConstruction },
                });
                Notify.info({ message: 'Project Updated' });
                await queryClient.invalidateQueries({
                  queryKey: [...ORG_SWITCHER_PROJECTS_KEY, rootStore.currentOrganizationId],
                });
                await queryClient.invalidateQueries({ queryKey: ['projects'] });
                homeMapStore.setPopupParams(undefined);
                await deleteProjectMapBoundsInLocalStorage(project.id);
                navigate({ to: '/home' });
              }}
            >
              Yes
            </Button>
          </DialogFooter>
        </>
      ),
    });
  }

  // Archive Project option
  items.push({
    type: 'dialog',
    label: <span className="text-red-500">Archive</span>,
    isAllowedTo: Action.ArchiveProject,
    dialogTitle: 'Confirm',
    dialogContent: (close) => (
      <>
        <DialogBody className="text-sm">
          This action will permanently archive the project. Are you sure you want to continue?
        </DialogBody>
        <DialogFooter>
          <Button
            variant="secondary"
            onClick={close}
            className="min-w-20"
            data-testid="project-details-more-archive-no"
          >
            No
          </Button>
          <Button
            variant="danger"
            className="min-w-20"
            data-testid="project-details-more-archive-yes"
            onClick={async () => {
              close();
              await archiveProject(project.id);
              Notify.info({ message: 'Project Archived' });
              await queryClient.invalidateQueries({
                queryKey: [...ORG_SWITCHER_PROJECTS_KEY, rootStore.currentOrganizationId],
              });
              await queryClient.invalidateQueries({ queryKey: ['projects'] });
              homeMapStore.setPopupParams(undefined);
              await deleteProjectMapBoundsInLocalStorage(project.id);
              navigate({ to: '/home' });
            }}
          >
            Yes
          </Button>
        </DialogFooter>
      </>
    ),
  });

  if (project.sandboxProject) {
    return null;
  }

  return (
    <Dropdown
      triggerElement={
        <IconButton
          variant="hollow"
          size="icon"
          icon="dotsVertical"
          iconSize={16}
          className="mr-1"
          aria-label="More actions"
          data-testid="project-details-more-menu"
        />
      }
      items={items}
    />
  );
}
